import React, { useState, useEffect } from 'react';
import { FiX, FiCheck, FiXCircle } from 'react-icons/fi';
import { decrypt, decryptArray, decryptQuestionData } from '../utils/decryption';
import { userRequest } from '../utils/requestMethods';
import { toast } from 'react-toastify';

const StudentTestAnswersModal = ({ isVisible, onClose, student, testDetails, answers, loading, onApprove, sessionId, onMarkSuccess }) => {
  const [approving, setApproving] = useState(false);
  const [markerComments, setMarkerComments] = useState({});
  const [savingScores, setSavingScores] = useState(null); // answerId being saved, or null
  const [scoreInputs, setScoreInputs] = useState({});
  const [localAnswers, setLocalAnswers] = useState(answers || []);

  // Question type filter state with persistence
  const [selectedQuestionType, setSelectedQuestionType] = useState(() => {
    const saved = localStorage.getItem('studentTestAnswersFilterType');
    return saved || 'all';
  });

  // Sync answers from prop on initial load (or when modal re-opens with new data)
  useEffect(() => {
    if (answers && answers.length > 0) {
      setLocalAnswers(answers);
      const initialScores = {};
      const initialComments = {};
      answers.forEach(answer => {
        if (answer.requires_manual_marking) {
          if (answer.score_awarded !== undefined && answer.score_awarded !== null) {
            initialScores[answer._id] = answer.score_awarded.toString();
          }
          if (answer.marker_comment) {
            initialComments[answer._id] = answer.marker_comment;
          }
        }
      });
      setScoreInputs(prev => ({ ...prev, ...initialScores }));
      setMarkerComments(prev => ({ ...prev, ...initialComments }));
    }
  }, [answers]);

  if (!isVisible) return null;

  const handleApprove = async () => {
    if (!onApprove) return;
    
    setApproving(true);
    try {
      await onApprove(student);
    } catch (error) {
      console.error('Error approving result:', error);
    } finally {
      setApproving(false);
    }
  };

  const handleMarkQuestion = async (answerId, scoreExpected) => {
    if (!sessionId) {
      toast.error('Session ID not available');
      return;
    }

    // Get the input score or default to expected score
    // Check if scoreInputs[answerId] exists and is not empty, otherwise use expected score
    // Note: We check for undefined/null/empty string, but allow 0 as a valid score
    const scoreInputValue = scoreInputs[answerId];
    const inputScore = (scoreInputValue !== undefined && scoreInputValue !== null && scoreInputValue !== '') 
      ? parseFloat(scoreInputValue) 
      : scoreExpected;
    
    // Validate that the input score is a valid number
    if (isNaN(inputScore)) {
      toast.error('Please enter a valid score');
      return;
    }
    
    // Validate that the input score doesn't exceed the expected score
    if (inputScore > scoreExpected) {
      toast.error(`Score cannot exceed the maximum of ${scoreExpected} points`);
      return;
    }
    
    // Validate that the input score is not negative
    if (inputScore < 0) {
      toast.error('Score cannot be negative');
      return;
    }

    setSavingScores(answerId);
    try {
      const comment = markerComments[answerId]?.trim();
      const answerPayload = { answer_id: answerId, score_awarded: inputScore };
      if (comment) answerPayload.marker_comment = comment;

      await userRequest.patch(`/test-session/${sessionId}/mark-answers`, {
        answers: [answerPayload]
      });

      toast.success('Question marked successfully!');

      // Update the answer in local state — no modal reload needed
      setLocalAnswers(prev => prev.map(a =>
        a._id === answerId
          ? { ...a, manually_marked: true, score_awarded: inputScore, marker_comment: comment || a.marker_comment, marked_at: new Date().toISOString() }
          : a
      ));
      // Reflect saved values in inputs so hasUnsavedChanges returns false
      setScoreInputs(prev => ({ ...prev, [answerId]: inputScore.toString() }));
      setMarkerComments(prev => ({ ...prev, [answerId]: comment || '' }));

      // Silently refresh the parent table stats in the background
      if (onMarkSuccess) onMarkSuccess();
    } catch (error) {
      console.error('Error marking question:', error);
      toast.error('Failed to mark question');
    } finally {
      setSavingScores(null);
    }
  };

  const handleCommentChange = (answerId, value) => {
    setMarkerComments(prev => ({
      ...prev,
      [answerId]: value
    }));
  };

  const handleScoreChange = (answerId, value) => {
    setScoreInputs(prev => ({
      ...prev,
      [answerId]: value
    }));
  };

  // Quick score presets
  const handleQuickScore = (answerId, scoreExpected, preset) => {
    let score = 0;
    if (preset === 'half') {
      score = scoreExpected / 2;
    } else if (preset === 'full') {
      score = scoreExpected;
    }
    setScoreInputs(prev => ({
      ...prev,
      [answerId]: score.toString()
    }));
  };

  // Check if there are unsaved changes for an answer
  const hasUnsavedChanges = (answer) => {
    // For first-time marking, always allow saving (no original values to compare)
    if (!answer.manually_marked) {
      return true;
    }
    
    const currentScore = scoreInputs[answer._id] !== undefined 
      ? parseFloat(scoreInputs[answer._id]) 
      : (answer.score_awarded !== undefined && answer.score_awarded !== null ? answer.score_awarded : null);
    const currentComment = markerComments[answer._id] !== undefined 
      ? markerComments[answer._id] 
      : (answer.marker_comment || '');
    
    const originalScore = answer.score_awarded !== undefined && answer.score_awarded !== null ? answer.score_awarded : null;
    const originalComment = answer.marker_comment || '';
    
    // Check if score or comment has changed
    const scoreChanged = currentScore !== originalScore;
    const commentChanged = currentComment !== originalComment;
    
    return scoreChanged || commentChanged;
  };

  // Reset changes for an answer
  const handleResetChanges = (answer) => {
    setScoreInputs(prev => {
      const newState = { ...prev };
      if (answer.score_awarded !== undefined && answer.score_awarded !== null) {
        newState[answer._id] = answer.score_awarded.toString();
      } else {
        delete newState[answer._id];
      }
      return newState;
    });
    setMarkerComments(prev => {
      const newState = { ...prev };
      if (answer.marker_comment) {
        newState[answer._id] = answer.marker_comment;
      } else {
        delete newState[answer._id];
      }
      return newState;
    });
  };

  // Handle question type filter change
  const handleQuestionTypeFilter = (type) => {
    setSelectedQuestionType(type);
    localStorage.setItem('studentTestAnswersFilterType', type);
  };

  // Filter answers based on selected question type
  const filteredAnswers = selectedQuestionType === 'all'
    ? localAnswers
    : localAnswers.filter(answer => answer.question_type === selectedQuestionType);

  // Get question type counts
  const questionTypeCounts = {
    all: localAnswers.length,
    multiple_choice: localAnswers.filter(a => a.question_type === 'multiple_choice').length,
    true_or_false: localAnswers.filter(a => a.question_type === 'true_or_false').length,
    text: localAnswers.filter(a => a.question_type === 'text').length,
  };

  const getAnswerStatus = (answer) => {
    if (!answer.answer) return 'unanswered';
    
    if (answer.is_correct === true) {
      return 'correct';
    }
    return 'incorrect';
  };

  const getAnswerStatusIcon = (status) => {
    switch (status) {
      case 'correct':
        return <FiCheck className="w-5 h-5 text-green-600" />;
      case 'incorrect':
        return <FiXCircle className="w-5 h-5 text-red-600" />;
      default:
        return <FiX className="w-5 h-5 text-gray-400" />;
    }
  };

  const getAnswerStatusColor = (status) => {
    switch (status) {
      case 'correct':
        return 'bg-green-50 border-green-200';
      case 'incorrect':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getAnswerStatusText = (status) => {
    switch (status) {
      case 'correct':
        return 'Correct';
      case 'incorrect':
        return 'Incorrect';
      default:
        return 'Unanswered';
    }
  };

  const getQuestionTypeDisplay = (questionType) => {
    switch (questionType) {
      case 'multiple_choice':
        return 'Multiple Choice';
      case 'true_or_false':
        return 'True/False';
      case 'text':
        return 'Short Answer';
      default:
        return questionType?.replace('_', ' ') || 'Unknown';
    }
  };

  const getQuestionTypeColor = (questionType) => {
    switch (questionType) {
      case 'multiple_choice':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'true_or_false':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'text':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <style>{`
        .question-content h1,
        .question-content h2,
        .question-content h3 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0.5rem 0;
          color: rgb(30, 41, 59);
        }
        .question-content p {
          margin: 0.5rem 0;
        }
        .question-content img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 0.5rem 0;
          border-radius: 0.5rem;
        }
        .question-content br {
          line-height: 1.5;
        }
        .question-content b,
        .question-content strong {
          font-weight: 600;
        }
        .question-content i,
        .question-content em {
          font-style: italic;
        }
        .question-content u {
          text-decoration: underline;
        }
        .question-content span {
          display: inline;
        }
      `}</style>
        <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-slate-800">
                Test Answers - {student?.child?.firstname} {student?.child?.lastname}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {testDetails?.title} - {testDetails?.subject?.subject_name}
              </p>
              
              {/* Test Performance Overview */}
              <div className="mt-4 flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-slate-600">
                    Score: <span className="font-semibold text-slate-800">
                      {localAnswers.reduce((sum, answer) => sum + (answer.score_awarded || 0), 0).toFixed(2)}/{localAnswers.reduce((sum, answer) => sum + (answer.score_expected || 0), 0).toFixed(2)}
                    </span>
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="text-sm text-slate-600">
                    Time: <span className="font-semibold text-slate-800">
                      {Math.round(localAnswers.reduce((sum, answer) => sum + (answer.time_taken || 0), 0) / 1000)}s
                    </span>
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-slate-600">
                    Progress: <span className="font-semibold text-slate-800">
                      {student?.progress || 0}%
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-slate-600">Loading answers...</span>
            </div>
          ) : localAnswers.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto h-16 w-16 text-slate-300 mb-4 flex items-center justify-center">
                <FiX className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No answers found</h3>
              <p className="text-slate-500 text-sm">This student hasn't answered any questions yet.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Overall Progress Bar */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800">Overall Performance</h3>
                  <span className="text-sm text-slate-600">
                    {localAnswers.filter(answer => answer.is_correct === true).length} of {answers.length} correct
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 h-3 rounded-full transition-all duration-500" 
                    style={{ 
                      width: `${(localAnswers.filter(answer => answer.is_correct === true).length / answers.length) * 100}%` 
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Score: {localAnswers.reduce((sum, answer) => sum + (answer.score_awarded || 0), 0).toFixed(2)}/{localAnswers.reduce((sum, answer) => sum + (answer.score_expected || 0), 0).toFixed(2)}</span>
                  <span>Time: {Math.round(localAnswers.reduce((sum, answer) => sum + (answer.time_taken || 0), 0) / 1000)}s</span>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-7 gap-4 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-700">
                    {localAnswers.filter(answer => answer.is_correct === true).length}
                  </div>
                  <div className="text-sm text-green-600">Correct</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-700">
                    {localAnswers.filter(answer => answer.is_correct === false).length}
                  </div>
                  <div className="text-sm text-red-600">Incorrect</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-gray-700">
                    {localAnswers.filter(answer => !answer.answer).length}
                  </div>
                  <div className="text-sm text-gray-600">Unanswered</div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-orange-700">
                    {localAnswers.filter(answer => answer.requires_manual_marking && !answer.manually_marked).length}
                  </div>
                  <div className="text-sm text-orange-600">Pending Manual Marking</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-700">
                    {localAnswers.filter(answer => answer.manually_marked).length}
                  </div>
                  <div className="text-sm text-green-600">Manually Marked</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-700">
                    {localAnswers.reduce((sum, answer) => sum + (answer.score_awarded || 0), 0).toFixed(2)}
                  </div>
                  <div className="text-sm text-blue-600">Total Score</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-700">
                    {Math.round(localAnswers.reduce((sum, answer) => sum + (answer.time_taken || 0), 0) / 1000)}s
                  </div>
                  <div className="text-sm text-purple-600">Total Time</div>
                </div>
              </div>

              {/* Question Type Filter */}
              <div className="mb-6 p-4 bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-200 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-700">Filter by Question Type</h3>
                  <span className="text-xs text-slate-500">
                    Showing {filteredAnswers.length} of {answers.length} questions
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handleQuestionTypeFilter('all')}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedQuestionType === 'all'
                        ? 'bg-slate-800 text-white shadow-md'
                        : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    <span>All Types</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      selectedQuestionType === 'all'
                        ? 'bg-slate-700 text-white'
                        : 'bg-slate-200 text-slate-600'
                    }`}>
                      {questionTypeCounts.all}
                    </span>
                  </button>
                  
                  <button
                    onClick={() => handleQuestionTypeFilter('multiple_choice')}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedQuestionType === 'multiple_choice'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-white text-blue-700 border border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    <span>Multiple Choice</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      selectedQuestionType === 'multiple_choice'
                        ? 'bg-blue-500 text-white'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {questionTypeCounts.multiple_choice}
                    </span>
                  </button>
                  
                  <button
                    onClick={() => handleQuestionTypeFilter('true_or_false')}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedQuestionType === 'true_or_false'
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-white text-green-700 border border-green-300 hover:bg-green-50'
                    }`}
                  >
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    <span>True/False</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      selectedQuestionType === 'true_or_false'
                        ? 'bg-green-500 text-white'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {questionTypeCounts.true_or_false}
                    </span>
                  </button>
                  
                  <button
                    onClick={() => handleQuestionTypeFilter('text')}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedQuestionType === 'text'
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'bg-white text-indigo-700 border border-indigo-300 hover:bg-indigo-50'
                    }`}
                  >
                    <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                    <span>Short Answer</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      selectedQuestionType === 'text'
                        ? 'bg-indigo-500 text-white'
                        : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      {questionTypeCounts.text}
                    </span>
                  </button>
                </div>
              </div>

              {/* Questions and Answers */}
              {filteredAnswers.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="mx-auto h-16 w-16 text-slate-300 mb-4 flex items-center justify-center">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No questions found</h3>
                  <p className="text-slate-500 text-sm">No questions match the selected filter.</p>
                </div>
              ) : (
                filteredAnswers.map((answer, index) => {
                const status = getAnswerStatus(answer);
                const decryptedQuestion = decrypt(answer.question_text);
                const decryptedOptions = decryptArray(answer.question_options || []);
                const decryptedCorrectAnswer = decrypt(answer.question_answer);
                
                return (
                  <div key={answer._id || index} className={`border rounded-lg p-6 ${getAnswerStatusColor(status)}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="bg-white text-slate-700 font-semibold rounded-full w-8 h-8 flex items-center justify-center text-sm">
                          {index + 1}
                        </span>
                        <div className="flex items-center space-x-2">
                          {getAnswerStatusIcon(status)}
                          <span className={`text-sm font-medium ${
                            status === 'correct' ? 'text-green-700' :
                            status === 'incorrect' ? 'text-red-700' : 'text-gray-700'
                          }`}>
                            {getAnswerStatusText(status)}
                          </span>
                        </div>
                        
                        {/* Question Type Badge */}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getQuestionTypeColor(answer.question_type)}`}>
                          {getQuestionTypeDisplay(answer.question_type)}
                        </span>
                        
                        {/* Manual Marking Indicator */}
                        {answer.requires_manual_marking && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Manual Marking Required
                          </span>
                        )}
                      </div>
                      
                      {/* Score and Time Info */}
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-slate-600">Score:</span>
                          <span className="font-semibold text-slate-800">
                            {answer.score_awarded || 0}/{answer.score_expected || 0}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                          <span className="text-slate-600">Time:</span>
                          <span className="font-semibold text-slate-800">
                            {Math.round((answer.time_taken || 0) / 1000)}s
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Question */}
                    <div className="mb-4">
                      <h3 className="font-semibold text-slate-800 mb-2">Question:</h3>
                      <div 
                        className="text-slate-700 leading-relaxed prose prose-sm max-w-none question-content"
                        dangerouslySetInnerHTML={{ __html: decryptedQuestion }}
                        style={{
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      />
                    </div>

                    {/* Options */}
                    {answer.question_type === 'multiple_choice' && decryptedOptions.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-slate-700 mb-2">Options:</h4>
                        <div className="space-y-2">
                          {decryptedOptions.map((option, optionIndex) => {
                            const isCorrect = option === decryptedCorrectAnswer;
                            const isSelected = answer?.answer === option;
                            
                            return (
                              <div
                                key={optionIndex}
                                className={`flex items-center space-x-3 p-3 rounded-lg border ${
                                  isCorrect
                                    ? 'bg-green-100 border-green-300 text-green-800'
                                    : isSelected && !isCorrect
                                    ? 'bg-red-100 border-red-300 text-red-800'
                                    : 'bg-white border-gray-200 text-slate-700'
                                }`}
                              >
                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  isCorrect
                                    ? 'border-green-500 bg-green-500'
                                    : isSelected && !isCorrect
                                    ? 'border-red-500 bg-red-500'
                                    : 'border-gray-300'
                                }`}>
                                  {isCorrect && <FiCheck className="w-3 h-3 text-white" />}
                                  {isSelected && !isCorrect && <FiX className="w-3 h-3 text-white" />}
                                </div>
                                <span className="flex-1">{option}</span>
                                {isCorrect && (
                                  <span className="text-xs font-medium text-green-600 bg-green-200 px-2 py-1 rounded">
                                    Correct Answer
                                  </span>
                                )}
                                {isSelected && !isCorrect && (
                                  <span className="text-xs font-medium text-red-600 bg-red-200 px-2 py-1 rounded">
                                    Student's Answer
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* True/False Questions */}
                    {answer.question_type === 'true_or_false' && (
                      <div className="mb-4">
                        <h4 className="font-medium text-slate-700 mb-2">Answer:</h4>
                        <div className="space-y-2">
                          <div className={`flex items-center space-x-3 p-3 rounded-lg border ${
                            decryptedCorrectAnswer === 'true'
                              ? 'bg-green-100 border-green-300 text-green-800'
                              : 'bg-white border-gray-200 text-slate-700'
                          }`}>
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              decryptedCorrectAnswer === 'true'
                                ? 'border-green-500 bg-green-500'
                                : 'border-gray-300'
                            }`}>
                              {decryptedCorrectAnswer === 'true' && <FiCheck className="w-3 h-3 text-white" />}
                            </div>
                            <span>True</span>
                            {decryptedCorrectAnswer === 'true' && (
                              <span className="text-xs font-medium text-green-600 bg-green-200 px-2 py-1 rounded">
                                Correct Answer
                              </span>
                            )}
                          </div>
                          <div className={`flex items-center space-x-3 p-3 rounded-lg border ${
                            decryptedCorrectAnswer === 'false'
                              ? 'bg-green-100 border-green-300 text-green-800'
                              : 'bg-white border-gray-200 text-slate-700'
                          }`}>
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              decryptedCorrectAnswer === 'false'
                                ? 'border-green-500 bg-green-500'
                                : 'border-gray-300'
                            }`}>
                              {decryptedCorrectAnswer === 'false' && <FiCheck className="w-3 h-3 text-white" />}
                            </div>
                            <span>False</span>
                            {decryptedCorrectAnswer === 'false' && (
                              <span className="text-xs font-medium text-green-600 bg-green-200 px-2 py-1 rounded">
                                Correct Answer
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Text Questions (Short Answer/Descriptive) */}
                    {answer.question_type === 'text' && (
                      <div className="mb-4">
                        <h4 className="font-medium text-slate-700 mb-2">Scoring Guide:</h4>
                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                          <p className="text-slate-700 text-sm leading-relaxed">
                            {decryptedCorrectAnswer || 'No scoring guide provided'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Student's Answer Details */}
                    <div className="mt-4 space-y-3">
                      {answer.answer && (
                        <div className={`p-4 border rounded-lg ${
                          answer.requires_manual_marking 
                            ? 'bg-orange-50 border-orange-200' 
                            : 'bg-blue-50 border-blue-200'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className={`font-medium ${
                              answer.requires_manual_marking ? 'text-orange-800' : 'text-blue-800'
                            }`}>
                              Student's Answer
                              {answer.requires_manual_marking && (
                                <span className="ml-2 text-xs font-normal text-orange-600">
                                  (Requires Manual Review)
                                </span>
                              )}
                            </h4>
                            <div className="flex items-center space-x-3 text-sm">
                              <span className={answer.requires_manual_marking ? 'text-orange-600' : 'text-blue-600'}>
                                Score: <span className="font-semibold">{answer.score_awarded || 0}</span>
                              </span>
                              <span className={answer.requires_manual_marking ? 'text-orange-600' : 'text-blue-600'}>
                                Time: <span className="font-semibold">{Math.round((answer.time_taken || 0) / 1000)}s</span>
                              </span>
                            </div>
                          </div>
                          <p className={`font-medium ${
                            answer.requires_manual_marking ? 'text-orange-700' : 'text-blue-700'
                          }`}>
                            {answer.answer}
                          </p>
                          
                          {/* Manual Marking Section */}
                          {answer.requires_manual_marking && (
                            <div className="mt-4 space-y-4">
                              {/* Show previous marking info if already marked */}
                              {answer.manually_marked && (
                                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg shadow-sm">
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-start space-x-3">
                                      <div className="flex-shrink-0 mt-0.5">
                                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                          <FiCheck className="w-5 h-5 text-green-600" />
                                        </div>
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-2">
                                          <h4 className="font-semibold text-green-800">Previously Marked</h4>
                                          {hasUnsavedChanges(answer) && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1.5 animate-pulse"></span>
                                              Unsaved Changes
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-sm text-green-700 mb-3">
                                          This question has been reviewed. You can update the score and comment below.
                                        </p>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                          <div className="bg-white/60 rounded-lg p-2.5">
                                            <span className="text-xs font-medium text-slate-500 block mb-1">Marked by</span>
                                            <p className="text-slate-800 font-medium">{answer.marked_by?.name || '-'}</p>
                                            {answer.marked_by?.email && (
                                              <p className="text-xs text-slate-500 mt-0.5">{answer.marked_by.email}</p>
                                            )}
                                          </div>
                                          <div className="bg-white/60 rounded-lg p-2.5">
                                            <span className="text-xs font-medium text-slate-500 block mb-1">Marked at</span>
                                            <p className="text-slate-800 font-medium">
                                              {answer.marked_at ? new Date(answer.marked_at).toLocaleString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              }) : 'Unknown'}
                                            </p>
                                          </div>
                                        </div>
                                        
                                        {answer.marker_comment && (
                                          <div className="mt-3 p-3 bg-white/80 border border-green-200 rounded-lg">
                                            <span className="text-xs font-medium text-slate-500 block mb-1">Previous Comment</span>
                                            <p className="text-sm text-slate-700 leading-relaxed">{answer.marker_comment}</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Manual Marking Interface */}
                              <div className="bg-gradient-to-br from-white to-slate-50 border-2 border-orange-200 rounded-xl shadow-lg p-5">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
                                  <div className="flex items-center space-x-3">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                      answer.manually_marked 
                                        ? 'bg-blue-100 text-blue-600' 
                                        : 'bg-orange-100 text-orange-600'
                                    }`}>
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </div>
                                    <div>
                                      <h5 className="font-semibold text-slate-800">
                                        {answer.manually_marked ? 'Update Marking' : 'Manual Marking'}
                                      </h5>
                                      <p className="text-xs text-slate-500">
                                        {answer.manually_marked ? 'Modify the score or add feedback' : 'Review and score this answer'}
                                      </p>
                                    </div>
                                  </div>
                                  {answer.manually_marked && hasUnsavedChanges(answer) && (
                                    <button
                                      onClick={() => handleResetChanges(answer)}
                                      className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1 hover:bg-slate-100 rounded transition-colors"
                                      title="Reset to original values"
                                    >
                                      Reset
                                    </button>
                                  )}
                                </div>

                                {/* Score Section */}
                                <div className="mb-4">
                                  <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-semibold text-slate-700">
                                      Score to Award
                                    </label>
                                    <span className="text-xs text-slate-500">
                                      Max: <span className="font-semibold text-slate-700">{answer.score_expected || 0}</span> pts
                                    </span>
                                  </div>
                                  
                                  {/* Quick Score Presets */}
                                  <div className="flex items-center space-x-2 mb-3">
                                    <span className="text-xs text-slate-500 mr-2">Quick:</span>
                                    <button
                                      onClick={() => handleQuickScore(answer._id, answer.score_expected, 'zero')}
                                      className="px-3 py-1 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                                    >
                                      0
                                    </button>
                                    <button
                                      onClick={() => handleQuickScore(answer._id, answer.score_expected, 'half')}
                                      className="px-3 py-1 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                                    >
                                      {answer.score_expected / 2}
                                    </button>
                                    <button
                                      onClick={() => handleQuickScore(answer._id, answer.score_expected, 'full')}
                                      className="px-3 py-1 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                                    >
                                      {answer.score_expected} (Full)
                                    </button>
                                  </div>

                                  {/* Score Input with Comparison */}
                                  <div className="relative">
                                    <input
                                      type="number"
                                      min="0"
                                      max={answer.score_expected || 0}
                                      step="0.5"
                                      value={scoreInputs[answer._id] !== undefined 
                                        ? scoreInputs[answer._id] 
                                        : (answer.score_awarded !== undefined && answer.score_awarded !== null 
                                          ? answer.score_awarded 
                                          : answer.score_expected || 0)}
                                      onChange={(e) => handleScoreChange(answer._id, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleMarkQuestion(answer._id, answer.score_expected);
                                        }
                                      }}
                                      className="w-full px-4 py-2 bg-white border-2 border-orange-300 rounded-lg text-lg font-bold text-orange-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                                      placeholder="Enter score"
                                    />
                                    
                                    {/* Score Comparison Indicator */}
                                    {answer.manually_marked && answer.score_awarded !== undefined && (
                                      <div className="absolute -right-2 -top-2">
                                        {(() => {
                                          const currentScore = scoreInputs[answer._id] !== undefined 
                                            ? parseFloat(scoreInputs[answer._id]) 
                                            : answer.score_awarded;
                                          const originalScore = answer.score_awarded;
                                          const diff = currentScore - originalScore;
                                          
                                          if (diff === 0) return null;
                                          
                                          return (
                                            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold ${
                                              diff > 0 
                                                ? 'bg-green-100 text-green-700 border border-green-300' 
                                                : 'bg-red-100 text-red-700 border border-red-300'
                                            }`}>
                                              <span>{diff > 0 ? '+' : ''}{diff.toFixed(1)}</span>
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>

                                  {/* Current Score Display */}
                                  {answer.manually_marked && answer.score_awarded !== undefined && (
                                    <div className="mt-2 flex items-center space-x-2 text-xs">
                                      <span className="text-slate-500">Current:</span>
                                      <span className="font-semibold text-slate-700">{answer.score_awarded}</span>
                                      {scoreInputs[answer._id] !== undefined && parseFloat(scoreInputs[answer._id]) !== answer.score_awarded && (
                                        <>
                                          <span className="text-slate-400">→</span>
                                          <span className="font-semibold text-orange-600">
                                            {parseFloat(scoreInputs[answer._id]) || 0}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Comment Section */}
                                <div className="mb-4">
                                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Marker Comment
                                    <span className="text-xs font-normal text-slate-400 ml-1">(Optional)</span>
                                  </label>
                                  <textarea
                                    value={markerComments[answer._id] !== undefined 
                                      ? markerComments[answer._id] 
                                      : (answer.marker_comment || '')}
                                    onChange={(e) => handleCommentChange(answer._id, e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                        e.preventDefault();
                                        handleMarkQuestion(answer._id, answer.score_expected);
                                      }
                                    }}
                                    className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm resize-none transition-all"
                                    rows="2"
                                    placeholder="Add feedback, comments, or suggestions for the student..."
                                  />
                                </div>

                                {/* Action Button */}
                                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-200">
                                  <button
                                    onClick={() => handleMarkQuestion(answer._id, answer.score_expected)}
                                    disabled={savingScores !== null || (answer.manually_marked && !hasUnsavedChanges(answer))}
                                    className={`inline-flex items-center px-5 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm ${
                                      (hasUnsavedChanges(answer) || !answer.manually_marked)
                                        ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-orange-200 hover:shadow-orange-300'
                                        : 'bg-slate-200 text-slate-600 cursor-not-allowed'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                  >
                                    {savingScores === answer._id ? (
                                      <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                        {answer.manually_marked ? 'Updating...' : 'Marking...'}
                                      </>
                                    ) : (
                                      <>
                                        <FiCheck className="w-4 h-4 mr-2" />
                                        {answer.manually_marked ? 'Update Mark' : 'Mark Question'}
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Performance Indicators */}
                      <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              answer.is_correct ? 'bg-green-500' : 'bg-red-500'
                            }`}></div>
                            <span className="text-sm text-slate-600">
                              {answer.is_correct ? 'Correct Answer' : 'Incorrect Answer'}
                            </span>
                          </div>
                          <div className="text-sm text-slate-500">
                            Expected Score: <span className="font-semibold text-slate-700">{answer.score_expected || 0}</span>
                          </div>
                        </div>
                        <div className="text-sm text-slate-500">
                          Question #{index + 1}
                        </div>
                      </div>
                    </div>
                  </div>
                );
                })
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            {student?.is_approved && (
              <div className="flex items-center space-x-2 text-green-600">
                <FiCheck className="w-4 h-4" />
                <span className="text-sm font-medium">Approved</span>
              </div>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              Close
            </button>
            {onApprove && !student?.is_approved && (
              <button
                onClick={handleApprove}
                disabled={approving}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors flex items-center"
              >
                {approving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Approving...
                  </>
                ) : (
                  <>
                    <FiCheck className="w-4 h-4 mr-2" />
                    Approve Result
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentTestAnswersModal;
